import React from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import apiService from '../../services/api.service';

/**
 * Settings > SAP Approval Workflows > SAP users. Every SAP B1 user of the company (read live)
 * with the PRPO user it belongs to (Users.SAPUserCode, one SAP user for all companies), and a
 * suggestion for the ones not linked yet (backend/src/services/sapUserLinks.util.ts). PRPO uses
 * the link to e-mail SAP approvers that have no e-mail in SAP, for saved SAP passwords, and as
 * the requester of purchase requests posted to SAP. A SAP user belongs to one active PRPO user.
 */

interface Brief { userid: number; username: string; fullname: string; email: string }
type How = 'email' | 'email-other-domain' | 'username' | 'name';
interface Suggestion { user: Brief; how: How; check: boolean; note: string }
interface Row {
  sap: { id: number; code: string; name: string; email: string; locked: boolean };
  system: boolean;
  status: 'linked' | 'duplicate' | 'inactive' | 'unlinked' | 'system';
  linked: Brief[];
  inactive: Brief[];
  emailDiffers: boolean;
  suggestions: Suggestion[];
}
interface Data {
  readAt: string;
  sapUsers: Row[];
  unknownCodes: Array<Brief & { code: string; reason: 'not-in-sap' | 'system' }>;
  withoutSap: Brief[];
  candidates: Brief[];
  summary: {
    sapUsers: number; people: number; system: number; linked: number; unlinked: number; suggested: number;
    duplicates: number; emailDiffers: number; unknownCodes: number; withoutSap: number;
  };
}

const HOW: Record<How, string> = {
  email: 'same e-mail',
  'email-other-domain': 'same e-mail name, other domain',
  username: 'PRPO username = SAP e-mail name',
  name: 'same name',
};

const errorText = (e: any, fallback: string) => String(e?.response?.data?.message || fallback);
const userLabel = (u: Brief) => `${u.fullname} (${u.username})`;
const needsAttention = (r: Row) =>
  r.status === 'unlinked' || r.status === 'inactive' || r.status === 'duplicate' || r.emailDiffers || (r.system && r.linked.length > 0);

const statusChip = (r: Row) => {
  if (r.system) return r.linked.length ? <Chip size="small" color="error" label="System account linked" /> : <Chip size="small" label="SAP system account" />;
  if (r.status === 'linked') return <Chip size="small" color="success" label="Linked" />;
  if (r.status === 'duplicate') return <Chip size="small" color="error" label="Duplicate" />;
  if (r.suggestions.length) return <Chip size="small" color="info" label="Suggestion" />;
  return <Chip size="small" color={r.status === 'inactive' ? 'warning' : 'default'} label="Not linked" />;
};

const SapUserLinks: React.FC<{ companyId: number | ''; companyName: string }> = ({ companyId, companyName }) => {
  const [data, setData] = React.useState<Data | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');
  const [msg, setMsg] = React.useState<{ severity: 'success' | 'error'; text: string } | null>(null);
  const [view, setView] = React.useState<'attention' | 'all'>('attention');
  // SAP user code -> PRPO user chosen in the picker (null = cleared); untouched rows use the suggestion.
  const [picked, setPicked] = React.useState<Record<string, Brief | null>>({});
  const [busy, setBusy] = React.useState('');

  // Ignore answers that arrive after another company was picked.
  const currentId = React.useRef<number | ''>('');
  currentId.current = companyId;

  const load = React.useCallback(async (id: number) => {
    setLoading(true); setError('');
    try {
      const res: any = await apiService.getSapUserLinks(id);
      if (Number(currentId.current) === id) { setData(res?.data || null); setPicked({}); }
    } catch (e: any) {
      if (Number(currentId.current) === id) setError(errorText(e, 'Could not read the SAP users'));
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    setData(null); setMsg(null);
    if (companyId) void load(Number(companyId));
  }, [companyId, load]);

  const chosen = (r: Row): Brief | null => (r.sap.code in picked ? picked[r.sap.code] : r.suggestions[0]?.user || null);

  const link = async (r: Row) => {
    const user = chosen(r);
    if (!companyId || !user) return;
    const suggestion = r.suggestions.find((s) => s.user.userid === user.userid);
    if (suggestion?.check && !window.confirm(
      `Link SAP user ${r.sap.name || r.sap.code} (${r.sap.code}) to ${userLabel(user)}?\n\nThe match is not conclusive (${HOW[suggestion.how]}${suggestion.note ? `: ${suggestion.note}` : ''}).`
    )) return;
    setBusy(r.sap.code); setMsg(null);
    try {
      const res: any = await apiService.linkSapUser(Number(companyId), user.userid, r.sap.code);
      setMsg({ severity: 'success', text: String(res?.message || 'Linked.') });
      await load(Number(companyId));
    } catch (e: any) {
      setMsg({ severity: 'error', text: errorText(e, 'Could not link the SAP user') });
    } finally {
      setBusy('');
    }
  };

  const unlink = async (user: Brief, code: string) => {
    if (!companyId) return;
    if (!window.confirm(`Remove SAP user ${code} from ${userLabel(user)}?`)) return;
    setBusy(`${code}-${user.userid}`); setMsg(null);
    try {
      await apiService.unlinkSapUser(Number(companyId), user.userid, code);
      setMsg({ severity: 'success', text: `SAP user ${code} is no longer linked to ${user.fullname}.` });
      await load(Number(companyId));
    } catch (e: any) {
      setMsg({ severity: 'error', text: errorText(e, 'Could not remove the link') });
    } finally {
      setBusy('');
    }
  };

  const holderLine = (u: Brief, code: string, warn?: string) => (
    <Stack key={u.userid} direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
      <Box>
        <Typography variant="body2">{userLabel(u)}</Typography>
        <Typography variant="caption" color="text.secondary">{u.email || 'no e-mail'}</Typography>
        {warn ? <Typography variant="caption" color="warning.main" display="block">{warn}</Typography> : null}
      </Box>
      <Button size="small" color="error" onClick={() => unlink(u, code)} disabled={!!busy}>
        {busy === `${code}-${u.userid}` ? 'Removing…' : 'Unlink'}
      </Button>
    </Stack>
  );

  const prpoCell = (r: Row) => {
    if (r.linked.length) {
      const warn = r.system ? 'A SAP system account should not be linked to a PRPO user'
        : r.status === 'duplicate' ? 'A SAP user can belong to one PRPO user only'
          : r.emailDiffers ? 'The PRPO e-mail differs from SAP; SAP approval e-mails go to the SAP one' : '';
      return <Stack spacing={0.5}>{r.linked.map((u) => holderLine(u, r.sap.code, warn))}</Stack>;
    }
    if (r.system) return <Typography variant="caption" color="text.secondary">Not a person: no PRPO user</Typography>;
    const value = chosen(r);
    const suggestion = value ? r.suggestions.find((s) => s.user.userid === value.userid) : undefined;
    return (
      <Stack spacing={0.5}>
        <Autocomplete
          size="small"
          options={data?.candidates || []}
          value={value}
          onChange={(_e, v) => setPicked((p) => ({ ...p, [r.sap.code]: v }))}
          getOptionLabel={userLabel}
          isOptionEqualToValue={(a, b) => a.userid === b.userid}
          renderOption={(props, o) => {
            const { key, ...rest } = props as any;
            return (
              <li key={key} {...rest}>
                <Box>
                  <Typography variant="body2">{userLabel(o)}</Typography>
                  <Typography variant="caption" color="text.secondary">{o.email}</Typography>
                </Box>
              </li>
            );
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="Choose the PRPO user"
              inputProps={{ ...params.inputProps, 'aria-label': `PRPO user for SAP user ${r.sap.name || r.sap.code}` }}
            />
          )}
          sx={{ minWidth: 240 }}
        />
        {suggestion ? (
          <Typography variant="caption" color={suggestion.check ? 'warning.main' : 'success.main'}>
            Suggested: {HOW[suggestion.how]}{suggestion.check ? ` – check${suggestion.note ? ` (${suggestion.note})` : ''}` : ''}
          </Typography>
        ) : null}
        {r.inactive.length ? (
          <Typography variant="caption" color="text.secondary">
            Linked to inactive PRPO user {r.inactive.map(userLabel).join(', ')}
          </Typography>
        ) : null}
      </Stack>
    );
  };

  const rows = (data?.sapUsers || []).filter((r) => view === 'all' || needsAttention(r));
  const s = data?.summary;

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" mb={1.5}>
        Each SAP B1 user of {companyName || 'this company'} and the PRPO user it belongs to (User Management › SAP User Code; one SAP
        user per PRPO user, for all companies). PRPO uses the link to e-mail SAP approvers who have no e-mail in SAP, for their saved SAP
        password, and as the requester of purchase requests posted to SAP. Suggestions compare e-mails first, then names; confirm the ones
        marked &quot;check&quot;.
      </Typography>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'center' }} mb={1.5}>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel id="sap-users-view">Show</InputLabel>
          <Select labelId="sap-users-view" label="Show" value={view} onChange={(e) => setView(e.target.value as 'attention' | 'all')}>
            <MenuItem value="attention">Needs attention</MenuItem>
            <MenuItem value="all">All SAP users</MenuItem>
          </Select>
        </FormControl>
        <Button size="small" onClick={() => companyId && load(Number(companyId))} disabled={loading}>Refresh from SAP</Button>
        {loading ? <CircularProgress size={20} aria-label="Reading the SAP users" /> : null}
        {data ? (
          <Typography variant="body2" color="text.secondary">Read from SAP at {new Date(data.readAt).toLocaleTimeString()}</Typography>
        ) : null}
      </Stack>

      {error ? <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert> : null}
      {msg ? <Alert severity={msg.severity} sx={{ mb: 1.5 }} onClose={() => setMsg(null)}>{msg.text}</Alert> : null}

      {s ? (
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap mb={1.5} aria-label="Summary">
          <Chip size="small" color="success" variant="outlined" label={`${s.linked} of ${s.people} linked`} />
          <Chip size="small" color={s.unlinked ? 'warning' : 'default'} variant="outlined" label={`${s.unlinked} not linked${s.suggested ? ` (${s.suggested} with a suggestion)` : ''}`} />
          {s.duplicates ? <Chip size="small" color="error" variant="outlined" label={`${s.duplicates} duplicate`} /> : null}
          {s.emailDiffers ? <Chip size="small" color="warning" variant="outlined" label={`${s.emailDiffers} e-mail differs`} /> : null}
          {s.unknownCodes ? <Chip size="small" color="error" variant="outlined" label={`${s.unknownCodes} PRPO code not in this SAP`} /> : null}
          <Chip size="small" variant="outlined" label={`${s.system} SAP system accounts`} />
        </Stack>
      ) : null}

      {data ? (
        <TableContainer component={Paper} sx={{ overflowX: 'auto', mb: 3 }}>
          <Table size="small" aria-label="SAP users and their PRPO users">
            <TableHead>
              <TableRow>
                <TableCell>SAP user</TableCell>
                <TableCell>SAP e-mail</TableCell>
                <TableCell>PRPO user</TableCell>
                <TableCell>Status</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.sap.code} hover>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{r.sap.name || r.sap.code}</Typography>
                    <Typography variant="caption" color="text.secondary">{r.sap.code}</Typography>
                    {r.sap.locked ? <Chip size="small" label="Locked in SAP" sx={{ ml: 1 }} /> : null}
                  </TableCell>
                  <TableCell sx={{ wordBreak: 'break-word', minWidth: 160 }}>
                    {r.sap.email || (
                      <Typography variant="caption" color={r.system ? 'text.secondary' : 'warning.main'}>
                        {r.system ? '-' : r.linked.length ? 'none in SAP: approval e-mails go to the PRPO e-mail' : 'none in SAP or PRPO: approval e-mails cannot reach this user'}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell sx={{ minWidth: 260 }}>{prpoCell(r)}</TableCell>
                  <TableCell>{statusChip(r)}</TableCell>
                  <TableCell align="right">
                    {!r.system && !r.linked.length ? (
                      <Button size="small" variant="contained" onClick={() => link(r)} disabled={!!busy || !chosen(r)}>
                        {busy === r.sap.code ? 'Linking…' : 'Link'}
                      </Button>
                    ) : null}
                  </TableCell>
                </TableRow>
              ))}
              {!rows.length ? (
                <TableRow>
                  <TableCell colSpan={5}>
                    <Typography variant="body2" color="text.secondary">
                      {view === 'attention' ? 'Nothing needs attention: every SAP user that is a person is linked.' : 'No SAP users.'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : null}
            </TableBody>
          </Table>
        </TableContainer>
      ) : null}

      {data && data.unknownCodes.length ? (
        <Box mb={3}>
          <Typography component="h2" variant="subtitle1" sx={{ fontWeight: 700 }}>PRPO users with a SAP user code that is not a person in this SAP</Typography>
          <Typography variant="caption" color="text.secondary" display="block" mb={1}>
            A purchase request they post to SAP names this code as requester. Leave a code that belongs to another company&apos;s SAP.
          </Typography>
          <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
            <Table size="small" aria-label="PRPO users with an unknown SAP user code">
              <TableHead>
                <TableRow>
                  <TableCell>PRPO user</TableCell>
                  <TableCell>SAP user code</TableCell>
                  <TableCell>Why</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.unknownCodes.map((u) => (
                  <TableRow key={u.userid}>
                    <TableCell>{holderLine(u, u.code)}</TableCell>
                    <TableCell>{u.code}</TableCell>
                    <TableCell>{u.reason === 'system' ? 'A SAP system account' : 'Not a user of this company\'s SAP'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      ) : null}

      {data ? (
        <Accordion disableGutters TransitionProps={{ unmountOnExit: true }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle2">Active PRPO users of {companyName || 'this company'} without a SAP user ({data.withoutSap.length})</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="caption" color="text.secondary" display="block" mb={1}>
              They cannot be SAP approvers, and their purchase requests reach SAP without their own SAP user as requester. Link them from
              the table above once they have a SAP user.
            </Typography>
            <Stack spacing={0.5}>
              {data.withoutSap.map((u) => (
                <Typography key={u.userid} variant="body2">
                  {userLabel(u)} <Typography component="span" variant="caption" color="text.secondary">{u.email}</Typography>
                </Typography>
              ))}
              {!data.withoutSap.length ? <Typography variant="body2" color="text.secondary">None.</Typography> : null}
            </Stack>
          </AccordionDetails>
        </Accordion>
      ) : null}
    </Box>
  );
};

export default SapUserLinks;
